import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useSettingsStore from '../../store/useSettingsStore';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import SettingsDrawer from './SettingsDrawer';
import HorizontalNav from './HorizontalNav';
import PageBreadcrumb from '../ui/PageBreadcrumb';
import { cn } from '../../lib/utils';
import { Toaster } from 'sonner';

const MainLayout = ({ children }) => {
  const location = useLocation();
  const {
    layoutType,
    layoutContainer,
    sidebarMode,
    isSidebarOpen,
    toggleSidebar,
    headerType,
    footerType,
    rtl,
  } = useSettingsStore();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isCollapsed = sidebarMode === 'collapsed';
  const isIconOnly = sidebarMode === 'icon-only';

  const sidebarWidth = layoutType === 'horizontal' ? 0 : (!isSidebarOpen ? 0 : isIconOnly ? 72 : isCollapsed ? 88 : 260);

  return (
    <div
      className={cn(
        "min-h-screen text-foreground transition-colors duration-300 flex justify-center",
        (!isMobile && layoutContainer === 'boxed') ? "bg-muted/40 dark:bg-slate-950/30" : "bg-background",
        rtl ? "font-rtl" : "font-sans"
      )}
      dir={rtl ? 'rtl' : 'ltr'}
    >
      <div className={cn(
        "w-full min-h-screen bg-background flex flex-col transition-all duration-300 relative",
        (!isMobile && layoutContainer === 'boxed') && "max-w-[1440px] border-x border-border shadow-2xl"
      )}>
        {/* Sidebar */}
        {(!isMobile && layoutType === 'horizontal') ? null : <Sidebar />}

        {/* Mobile Overlay */}
        <AnimatePresence>
          {isMobile && isSidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-30 bg-background/80 backdrop-blur-sm"
              onClick={toggleSidebar}
            />
          )}
        </AnimatePresence>

        <motion.div
          animate={{
            marginLeft: !rtl && !isMobile ? sidebarWidth : 0,
            marginRight: rtl && !isMobile ? sidebarWidth : 0,
          }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className="flex flex-col min-h-screen"
        >
          {/* Header */}
          <Header />

          {/* Horizontal Navigation */}
          {layoutType === 'horizontal' && <HorizontalNav />}

          {/* Page content */}
          <main className="flex-1 overflow-auto">
            <div className="p-4 lg:p-6 max-w-[1800px] mx-auto w-full">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18, ease: 'easeInOut' }}
                  className="w-full"
                >
                  <PageBreadcrumb className="mb-4" />
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>

          {/* Footer */}
          {footerType !== 'hidden' && <Footer />}
        </motion.div>

        {/* Floating Settings Button */}
        <SettingsDrawer />
        
        {/* Sonner Toast Notifications */}
        <Toaster position="top-right" richColors />
      </div>
    </div>
  );
};

export default MainLayout;